import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import 'chart.js/auto';

const POHubHome = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [editedData, setEditedData] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleParse = () => {
    if (!file) {
      alert('Please select a PDF file first');
      return;
    }

    const dummyData = {
      poNumber: 'PO-2026-04567',
      poDate: '2026-01-12',
      buyerName: 'Tata Steel Limited',
      deliveryDate: '2026-02-28',
      lineItems: [
        { item: 'MS Plate 10mm', qty: 500, rate: 78.50 },
        { item: 'SS Pipe 50mm', qty: 120, rate: 450.00 },
        { item: 'Bearing 6205', qty: 200, rate: 185.00 },
        { item: 'Hydraulic Seal Kit', qty: 35, rate: 1200.00 },
      ],
    };

    setParsedData(dummyData);
    setEditedData({
      ...dummyData,
      lineItems: dummyData.lineItems.map(item => ({ ...item })),
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = editedData.lineItems.map((item, i) => {
      if (i === index) {
        const updated = { ...item };
        if (field === 'qty' || field === 'rate') {
          updated[field] = parseFloat(value) || 0;
        } else {
          updated[field] = value;
        }
        return updated;
      }
      return item;
    });

    setEditedData({ ...editedData, lineItems: newItems });
  };

  const handleAccept = () => {
    // console.log('Saved PO:', editedData);
    alert('PO Accepted & Saved Successfully!');
    setParsedData(null);
    setEditedData(null);
    setFile(null);
  };

  const handleReject = () => {
    if (confirm('Reject this PO extraction?')) {
      setParsedData(null);
      setEditedData(null);
      setFile(null);
    }
  };

  const totalPOs = 156;
  const pendingVerification = 12;
  const pendingApproval = 8;
  const delayedPOs = 5;
  const inProcess = totalPOs - pendingVerification - pendingApproval - delayedPOs;

  const chartData = {
    labels: ['Pending Verification', 'Pending Approval', 'In Process', 'Delayed'],
    datasets: [
      {
        data: [pendingVerification, pendingApproval, inProcess, delayedPOs],
        backgroundColor: ['#fbbf24', '#f97316', '#22c55e', '#ef4444'],
        borderColor: '#000',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#a3e635',
          padding: 20,
        },
      },
    },
  };

  const kpis = [
    { label: 'Total POs Received', value: totalPOs, icon: 'fa-file-invoice-dollar' },
    { label: 'Pending Verification', value: pendingVerification, icon: 'fa-hourglass-half' },
    { label: 'Pending Approval', value: pendingApproval, icon: 'fa-thumbs-up' },
    { label: 'Delayed POs', value: delayedPOs, icon: 'fa-exclamation-triangle' },
  ];

  const alerts = [
    { type: 'Delayed', message: 'PO-2026-04123 is delayed by 7 days' },
    { type: 'QC Issue', message: 'PO-2026-03987 has QC rejection on item SS Pipe' },
    { type: 'Missing Confirmation', message: 'PO-2026-04412 awaiting buyer confirmation' },
  ];

  const lifecycleSteps = ['Received', 'Verified', 'Approved', 'Dispatched', 'Delivered', 'QC Passed', 'Closed'];

  return (
    <div className="min-h-screen bg-black text-green-400">
      <header className="bg-gray-900 py-6 px-4 text-center border-b border-green-800">
        <h1 className="text-4xl font-bold text-green-500">POHub</h1>
        <p className="text-green-300 mt-2">Purchase Order Digital Lifecycle</p>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-12">
        {/* Dashboard Overview */}
        <section className="bg-gray-900 rounded-xl shadow-2xl p-6 border border-green-800">
          <h2 className="text-2xl font-bold text-green-500 mb-6 flex items-center">
            <i className="fa-solid fa-chart-line mr-3"></i> Dashboard Overview
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {kpis.map((kpi, index) => (
              <div key={index} className="bg-black/50 rounded-lg p-6 text-center border border-green-900">
                <i className={`fa-solid ${kpi.icon} text-4xl text-green-500 mb-3`}></i>
                <p className="text-4xl font-bold text-green-400">{kpi.value}</p>
                <p className="text-sm text-green-300 mt-2">{kpi.label}</p>
              </div>
            ))}
          </div>

          <div className="h-64 lg:h-80">
            <Pie data={chartData} options={chartOptions} />
          </div>
        </section>

        {/* Upload PO */}
        <section className="bg-gray-900 rounded-xl shadow-2xl p-6 border border-green-800">
          <h2 className="text-2xl font-bold text-green-500 mb-6 flex items-center">
            <i className="fa-solid fa-upload mr-3"></i> Upload New PO
          </h2>
          <p className="text-green-300 mb-6">Upload PO received via Email / WhatsApp (PDF)</p>

          <div className="border-4 border-dashed border-green-700 rounded-xl p-12 text-center hover:border-green-500 transition">
            <i className="fa-solid fa-cloud-upload-alt text-7xl text-green-500 mb-6"></i>
            <p className="text-xl text-green-400 mb-6">Drop PDF here or click to browse</p>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              className="block w-full text-green-400 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:bg-green-600 file:text-white file:font-bold hover:file:bg-green-700 cursor-pointer"
            />
          </div>

          {file && <p className="mt-4 text-center text-green-300">Selected: {file.name}</p>}

          <button
            onClick={handleParse}
            disabled={!file}
            className="mt-8 w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-5 rounded-lg text-xl transition flex items-center justify-center"
          >
            <i className="fa-solid fa-robot mr-3"></i>
            Parse PO with AI
          </button>
        </section>

        {/* AI Extracted Data Review */}
        {editedData && (
          <>
            <section className="bg-gray-900 rounded-xl shadow-2xl p-6 border border-green-800">
              <h2 className="text-2xl font-bold text-green-500 mb-6 flex items-center">
                <i className="fa-solid fa-eye mr-3"></i> Review AI Extracted Data
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-green-300 mb-2">PO Number</label>
                  <input
                    type="text"
                    value={editedData.poNumber}
                    onChange={(e) => setEditedData({ ...editedData, poNumber: e.target.value })}
                    className="w-full bg-gray-800 border border-green-700 rounded-lg px-4 py-3 focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-green-300 mb-2">PO Date</label>
                  <input
                    type="date"
                    value={editedData.poDate}
                    onChange={(e) => setEditedData({ ...editedData, poDate: e.target.value })}
                    className="w-full bg-gray-800 border border-green-700 rounded-lg px-4 py-3 focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-green-300 mb-2">Buyer Name</label>
                  <input
                    type="text"
                    value={editedData.buyerName}
                    onChange={(e) => setEditedData({ ...editedData, buyerName: e.target.value })}
                    className="w-full bg-gray-800 border border-green-700 rounded-lg px-4 py-3 focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-green-300 mb-2">Delivery Date</label>
                  <input
                    type="date"
                    value={editedData.deliveryDate}
                    onChange={(e) => setEditedData({ ...editedData, deliveryDate: e.target.value })}
                    className="w-full bg-gray-800 border border-green-700 rounded-lg px-4 py-3 focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>

              <h3 className="text-xl font-bold text-green-500 mb-4">Line Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-green-800">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left">Item</th>
                      <th className="px-4 py-3 text-center">Qty</th>
                      <th className="px-4 py-3 text-center">Rate (₹)</th>
                      <th className="px-4 py-3 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedData.lineItems.map((item, index) => (
                      <tr key={index} className="border-t border-green-900">
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={item.item}
                            onChange={(e) => handleLineItemChange(index, 'item', e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 focus:border-green-500 focus:outline-none"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => handleLineItemChange(index, 'qty', e.target.value)}
                            className="w-24 bg-gray-700 border border-gray-600 rounded px-2 py-1 focus:border-green-500 focus:outline-none"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                            className="w-32 bg-gray-700 border border-gray-600 rounded px-2 py-1 focus:border-green-500 focus:outline-none"
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {(item.qty * item.rate).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-800 font-bold">
                      <td colSpan="3" className="px-4 py-4 text-right">Total Amount (₹)</td>
                      <td className="px-4 py-4 text-right text-green-400">
                        {editedData.lineItems.reduce((sum, item) => sum + (item.qty * item.rate), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleAccept}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-lg text-xl transition flex items-center justify-center"
                >
                  <i className="fa-solid fa-check mr-3"></i> Accept & Save PO
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-lg text-xl transition flex items-center justify-center"
                >
                  <i className="fa-solid fa-times mr-3"></i> Reject
                </button>
              </div>
            </section>

            {/* Lifecycle Snapshot */}
            <section className="bg-gray-900 rounded-xl shadow-2xl p-6 border border-green-800">
              <h2 className="text-2xl font-bold text-green-500 mb-6 flex items-center">
                <i className="fa-solid fa-tasks mr-3"></i> PO Lifecycle Snapshot
              </h2>
              <div className="flex flex-wrap justify-center items-center gap-4 text-lg md:text-xl">
                {lifecycleSteps.map((step, index) => (
                  <React.Fragment key={index}>
                    <span className={`${index <= 1 ? 'text-green-500 font-bold' : 'text-gray-500'}`}>
                      {step}
                    </span>
                    {index < lifecycleSteps.length - 1 && (
                      <i className={`fa-solid fa-arrow-right mx-2 ${index < 1 ? 'text-green-500' : 'text-gray-600'}`}></i>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-center text-green-300 mt-6">Current status: Verified (Ready for Approval after Save)</p>
            </section>
          </>
        )}

        {/* Alerts & Exceptions */}
        <section className="bg-gray-900 rounded-xl shadow-2xl p-6 border border-green-800">
          <h2 className="text-2xl font-bold text-green-500 mb-6 flex items-center">
            <i className="fa-solid fa-bell mr-3"></i> Alerts & Exceptions
          </h2>

          {alerts.length > 0 ? (
            <div className="space-y-4">
              {alerts.map((alert, index) => (
                <div key={index} className="bg-red-950/50 border border-red-800 rounded-lg p-5">
                  <p className="font-bold text-red-400 flex items-center">
                    <i className="fa-solid fa-exclamation-circle mr-2"></i>
                    {alert.type}
                  </p>
                  <p className="text-green-300 mt-2">{alert.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No active alerts</p>
          )}
        </section>
      </main>
    </div>
  );
};

export default POHubHome;